import React from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import {
    Select as AriaSelect,
    Button,
    ListBox,
    ListBoxItem,
    Popover,
    SelectValue,
} from "react-aria-components";

export type SelectOption = { value: string; label: string };

export function SelectField<T extends FieldValues>(props: {
    control: Control<T>;
    name: FieldPath<T>;
    label: string;
    options: SelectOption[];
    disabled?: boolean;
    error?: string;
    placeholder?: string;
}) {
    const { control, name, label, options, disabled, error, placeholder } = props;
    const textWhenEmpty = placeholder ?? label;
    return (
        <div className="form__field">
            <div className="form__field__input">
                <Controller
                    control={control}
                    name={name}
                    render={({ field, fieldState }) => {
                        const raw = field.value;
                        const value = raw == null ? "" : String(raw);
                        const hasValue = value.trim().length > 0;

                        const errorMsg = error || fieldState.error?.message;
                        const invalid = !!errorMsg || fieldState.invalid;

                        return (
                            <AriaSelect
                                aria-label={label}
                                selectedKey={hasValue ? value : null}
                                onChange={(key) => field.onChange(String(key ?? ""))}
                                isDisabled={disabled}
                            >
                                {({ isOpen }) => (
                                    <div
                                        className={[
                                            "ds-input",
                                            "ds-input--select",
                                            isOpen ? "ds-input--active" : "",
                                            disabled ? "ds-input--disabled" : "",
                                            hasValue ? "ds-input--filled" : "",
                                            invalid ? "ds-input--error" : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        <Button className="ds-input__control ds-input__control--selectAria">
                                            <SelectValue className="ds-input__selectValue">
                                                {({ selectedText }) => selectedText || textWhenEmpty}
                                            </SelectValue>
                                            <span className="ds-input__chevron" aria-hidden />
                                        </Button>

                                        <Popover
                                            className="ds-select__popover"
                                            placement="bottom start"
                                            offset={6}
                                        >
                                            <ListBox className="ds-select__listbox">
                                                {options.map((o) => (
                                                    <ListBoxItem
                                                        key={o.value}
                                                        id={o.value}
                                                        textValue={o.label}
                                                        className="ds-select__option"
                                                    >
                                                        {o.label}
                                                    </ListBoxItem>
                                                ))}
                                            </ListBox>
                                        </Popover>
                                    </div>
                                )}
                            </AriaSelect>
                        );
                    }}
                />
            </div>

            {!!error && <div className="form__field__error">{error}</div>}
        </div>
    );
}